import { Injectable, inject, signal, computed, PLATFORM_ID, effect } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { firstValueFrom } from 'rxjs';
import { ApiService } from './api.service';
import { AuthService } from './auth.service';
import { ToastService } from './toast.service';
import { ELocalStorage } from '../enums/ELocalStorage';
import { EModelApiPaths } from '../enums/EModelApiPaths';
import { Cart, CartItem, AddToCartRequest } from '../../interfaces/cart.interface';

@Injectable({ providedIn: 'root' })
export class CartService {
  private api = inject(ApiService);
  private auth = inject(AuthService);
  private toast = inject(ToastService);
  private platformId = inject(PLATFORM_ID);

  cart = signal<Cart>({
    items: [],
    subtotal: 0,
    discount: 0,
    shipping_charge: 0,
    total: 0,
  });

  cartCount = computed(() => this.cart().items.reduce((sum, item) => sum + item.quantity, 0));
  cartTotal = computed(() => this.cart().total);

  // Was the last login transition handled? Used by the effect below to fire
  // exactly once per logged-out → logged-in flip.
  private lastLoggedIn = false;

  constructor() {
    // Whenever isLoggedIn flips false → true, push any local-storage cart
    // items to the BE and then re-fetch the canonical BE cart so the rest of
    // the app sees the same items as the server.
    effect(() => {
      const loggedIn = this.auth.isLoggedIn();
      if (!isPlatformBrowser(this.platformId)) return;
      if (loggedIn && !this.lastLoggedIn) {
        this.lastLoggedIn = true;
        this.syncLocalCartToBackend().then(() => this.fetchCart());
      } else if (!loggedIn) {
        this.lastLoggedIn = false;
      }
    });
  }

  /**
   * Pushes any items currently held in local storage (added while the user
   * was anonymous) into the server-side cart, then clears local storage. This
   * runs once per login. Best-effort — failures are reported via the global
   * error interceptor.
   */
  private async syncLocalCartToBackend(): Promise<void> {
    const local = this.cart();
    if (!local.items?.length) return;

    for (const item of local.items) {
      try {
        await firstValueFrom(this.api.callCustomApi<any>(EModelApiPaths.CART_MANAGE, 'POST', {
          action: 'add',
          product_id: item.product_id,
          color: item.color,
          size: item.size,
          qty: item.quantity,
        }));
      } catch (e) {
        // Skip failed items — the BE-managed cart is the source of truth from
        // here on, and the rest of the items will still be synced.
      }
    }

    if (isPlatformBrowser(this.platformId)) {
      localStorage.removeItem(ELocalStorage.CART);
    }
  }

  init(): void {
    if (!isPlatformBrowser(this.platformId)) return;

    if (this.auth.isLoggedIn()) {
      this.fetchCart();
    } else {
      const stored = localStorage.getItem(ELocalStorage.CART);
      if (stored) {
        try {
          const parsed: Cart = JSON.parse(stored);
          // Drop legacy items missing product info (sale_price/name)
          const validItems = (parsed.items || []).filter(it => it.sale_price > 0 && it.product_name);
          if (validItems.length !== (parsed.items || []).length) {
            this.updateLocalCart(validItems);
          } else {
            this.cart.set(parsed);
          }
        } catch {
          this.clearLocalCart();
        }
      }
    }
  }

  fetchCart(): void {
    this.api.callCustomApi<{ data: Cart }>(EModelApiPaths.CART).subscribe({
      next: (res) => {
        if (res?.data) {
          this.cart.set({
            items: res.data.items || [],
            subtotal: res.data.subtotal || 0,
            discount: res.data.discount || 0,
            shipping_charge: res.data.shipping_charge || 0,
            total: res.data.total || 0,
            coupon_code: res.data.coupon_code,
            coupon_discount: res.data.coupon_discount,
          });
        }
      },
    });
  }

  addToCart(request: AddToCartRequest): void {
    if (this.auth.isLoggedIn()) {
      this.api.callCustomApi<any>(EModelApiPaths.CART_MANAGE, 'POST', {
        action: 'add',
        product_id: request.product_id,
        color: request.color,
        color_name: request.color_name,
        size: request.size,
        qty: request.quantity,
      }).subscribe({
        next: () => {
          this.fetchCart();
          this.toast.success('Added to cart!');
        },
      });
    } else {
      const current = this.cart();
      const existingIndex = current.items.findIndex(
        (item) => item.product_id === request.product_id && item.color === request.color && item.size === request.size
      );

      const items = [...current.items];
      if (existingIndex >= 0) {
        const newQty = items[existingIndex].quantity + request.quantity;
        items[existingIndex] = {
          ...items[existingIndex],
          quantity: newQty,
          total: newQty * items[existingIndex].sale_price,
        };
      } else {
        items.push({
          product_id: request.product_id,
          product_name: request.product_name,
          product_slug: request.product_slug,
          product_image: request.product_image,
          brand_name: request.brand_name,
          color: request.color,
          color_name: request.color_name,
          size: request.size,
          quantity: request.quantity,
          mrp: request.mrp,
          sale_price: request.sale_price,
          total: request.quantity * request.sale_price,
        });
      }

      this.updateLocalCart(items);
      this.toast.success('Added to cart!');
    }
  }

  /**
   * Sets the exact quantity for one product/colour/size line (0 removes it).
   * Silent (no toast) — used by live editors like the bulk-order grid that
   * must stay in sync with the cart on every change.
   */
  setLineQuantity(req: AddToCartRequest, qty: number): void {
    const existing = this.cart().items.find(
      (it) => it.product_id === req.product_id && it.color === req.color && it.size === req.size,
    );

    if (this.auth.isLoggedIn()) {
      if (qty <= 0) {
        if (existing?._id) {
          this.api.callCustomApi<any>(EModelApiPaths.CART_MANAGE, 'POST', { action: 'remove', cart_id: existing._id })
            .subscribe({ next: () => this.fetchCart(), error: () => this.fetchCart() });
        }
      } else if (existing?._id) {
        this.api.callCustomApi<any>(EModelApiPaths.CART_MANAGE, 'POST', { action: 'update', cart_id: existing._id, qty })
          .subscribe({ next: () => this.fetchCart(), error: () => this.fetchCart() });
      } else {
        this.api.callCustomApi<any>(EModelApiPaths.CART_MANAGE, 'POST', {
          action: 'add', product_id: req.product_id, color: req.color, color_name: req.color_name, size: req.size, qty,
        }).subscribe({ next: () => this.fetchCart(), error: () => this.fetchCart() });
      }
      return;
    }

    // Anonymous: mutate the local-storage cart in place.
    const items = [...this.cart().items];
    const idx = items.findIndex(
      (it) => it.product_id === req.product_id && it.color === req.color && it.size === req.size,
    );
    if (qty <= 0) {
      if (idx >= 0) items.splice(idx, 1);
    } else if (idx >= 0) {
      items[idx] = { ...items[idx], quantity: qty, sale_price: req.sale_price, mrp: req.mrp, total: qty * req.sale_price };
    } else {
      items.push({
        product_id: req.product_id,
        product_name: req.product_name,
        product_slug: req.product_slug,
        product_image: req.product_image,
        brand_name: req.brand_name,
        color: req.color,
        color_name: req.color_name,
        size: req.size,
        quantity: qty,
        mrp: req.mrp,
        sale_price: req.sale_price,
        total: qty * req.sale_price,
      });
    }
    this.updateLocalCart(items);
  }

  updateQuantity(index: number, quantity: number): void {
    if (this.auth.isLoggedIn()) {
      const item = this.cart().items[index];
      this.api.callCustomApi<any>(EModelApiPaths.CART_MANAGE, 'POST', {
        action: 'update',
        cart_id: item._id,
        qty: quantity,
      }).subscribe({
        next: () => this.fetchCart(),
      });
    } else {
      const items = [...this.cart().items];
      items[index] = { ...items[index], quantity, total: quantity * items[index].sale_price };
      this.updateLocalCart(items);
    }
  }

  removeItem(index: number): void {
    if (this.auth.isLoggedIn()) {
      const item = this.cart().items[index];
      this.api.callCustomApi<any>(EModelApiPaths.CART_MANAGE, 'POST', {
        action: 'remove',
        cart_id: item._id,
      }).subscribe({
        next: () => {
          this.fetchCart();
          this.toast.success('Item removed from cart');
        },
      });
    } else {
      const items = this.cart().items.filter((_, i) => i !== index);
      this.updateLocalCart(items);
      this.toast.success('Item removed from cart');
    }
  }

  clearCart(): void {
    // Reset the in-memory cart immediately so the UI (header badge, cart page,
    // etc.) reflects an empty cart without waiting on the network.
    this.clearLocalCart();
    if (this.auth.isLoggedIn()) {
      // Best-effort server-side clear; we don't need to await the response.
      this.api.callCustomApi<any>(EModelApiPaths.CART_MANAGE, 'POST', { action: 'clear' }).subscribe();
    }
  }

  applyCoupon(code: string): void {
    const cart_total = this.cart().subtotal || 0;
    this.api.callCustomApi<{ data: { coupon_code: string; discount: number } }>(
      EModelApiPaths.APPLY_COUPON,
      'POST',
      { coupon_code: code, cart_total },
    ).subscribe({
      next: (res) => {
        const d = res?.data;
        if (!d) return;
        // Merge the discount into the existing cart — the endpoint only
        // validates the code and returns the discount amount; it doesn't
        // persist the coupon server-side, so we track it client-side and
        // pass it on at checkout.
        this.cart.update(cart => {
          const shipping = cart.shipping_charge || 0;
          const subtotal = cart.subtotal || 0;
          const discount = Math.min(d.discount || 0, subtotal);
          return {
            ...cart,
            coupon_code: d.coupon_code,
            coupon_discount: discount,
            total: Math.max(0, subtotal - discount + shipping),
          };
        });
        this.toast.success('Coupon applied!');
      },
    });
  }

  removeCoupon(): void {
    // The backend doesn't persist coupons, so just drop the client-side
    // tracking and recompute the total.
    this.cart.update(cart => {
      const shipping = cart.shipping_charge || 0;
      const subtotal = cart.subtotal || 0;
      return {
        ...cart,
        coupon_code: undefined,
        coupon_discount: 0,
        total: subtotal + shipping,
      };
    });
    this.toast.info('Coupon removed');
  }

  private updateLocalCart(items: CartItem[]): void {
    const subtotal = items.reduce((sum, item) => sum + item.total, 0);
    const cart: Cart = {
      items,
      subtotal,
      discount: 0,
      shipping_charge: subtotal > 999 ? 0 : 99,
      total: subtotal - 0 + (subtotal > 999 ? 0 : 99),
    };
    this.cart.set(cart);
    this.saveToLocal(cart);
  }

  private clearLocalCart(): void {
    this.cart.set({ items: [], subtotal: 0, discount: 0, shipping_charge: 0, total: 0 });
    if (isPlatformBrowser(this.platformId)) {
      localStorage.removeItem(ELocalStorage.CART);
    }
  }

  private saveToLocal(cart: Cart): void {
    if (isPlatformBrowser(this.platformId)) {
      localStorage.setItem(ELocalStorage.CART, JSON.stringify(cart));
    }
  }
}
